import type { ProductBankIdSignStatus, ProductSigningStatus } from "../hooks";
import { requiresSigning } from "../types/signing";
import { sleep } from "../utils";
import {
	cancelSignQuery,
	signStatusQuery,
	startSignQuery,
	verifySignQuery,
} from "./endpoints/signing";
import { sveaFetch, unwrapJsonResponse } from "./fetch";
import type { AuthStatusResponse, StartAuthResponse } from "./login";

type SigningTokenResponse = { signingToken: string };

export type SigningRequiredResponse<R = void> = SigningTokenResponse | R;

export const doRequestWithSigning = <T, A>(args: {
	baseUrl: string;
	appVersion: string;
	maxTime: number;
	request: (args: A) => Promise<SigningRequiredResponse<T>>;
	setIsSigning: (isSigning: boolean) => void;
	setBankIdToken: (token: string) => void;
	setOnExtendTime: (onExtendTime: () => void) => void;
	setQrCode: (qrCode: string | null) => void;
	resetSigningState: () => void;
	setStartTime: (time: number) => void;
	setOnCancel: (onCancel: () => void) => void;
}) => {
	const {
		baseUrl,
		appVersion,
		maxTime,
		request,
		setIsSigning,
		setBankIdToken,
		setOnExtendTime,
		setQrCode,
		resetSigningState,
		setStartTime,
		setOnCancel,
	} = args;

	const startSign = async (token: string) => {
		const startSigning = startSignQuery(token);

		return (await unwrapJsonResponse(
			await sveaFetch(baseUrl, startSigning.url, appVersion, {
				method: startSigning.method,
				body: JSON.stringify(startSigning.body),
				withAuth: true,
			}),
		)) as StartAuthResponse;
	};

	const signStatus = async (token: string) => {
		const signStatusReq = signStatusQuery(token);
		return (await unwrapJsonResponse(
			await sveaFetch(baseUrl, signStatusReq.url, appVersion, {
				method: signStatusReq.method,
				body: JSON.stringify(signStatusReq.body),
				withAuth: true,
			}),
		)) as AuthStatusResponse;
	};

	const cancelSign = async (token: string) => {
		const cancelSigning = cancelSignQuery(token);

		await sveaFetch(baseUrl, cancelSigning.url, appVersion, {
			method: cancelSigning.method,
			body: JSON.stringify(cancelSigning.body),
			withAuth: true,
		});
	};

	const verifySign = async (token: string) => {
		const verifySignReq = verifySignQuery(token);
		const response = await sveaFetch(baseUrl, verifySignReq.url, appVersion, {
			method: verifySignReq.method,
			body: JSON.stringify(verifySignReq.body),
			withAuth: true,
		});
		return unwrapJsonResponse(response) as T;
	};

	const startOrder = async (signingToken: string, startTime: number) => {
		let keepCalling = true;
		let status: "successful" | "pending" = "pending";

		setTimeout(() => {
			keepCalling = false;
		}, 29000);

		while (keepCalling) {
			const { bankIdToken: startSignBankIdToken } = await startSign(signingToken);
			setBankIdToken(startSignBankIdToken);
			setOnCancel?.(() => () => cancelSign(startSignBankIdToken));

			while (keepCalling) {
				const statusResponse = await signStatus(startSignBankIdToken);
				setQrCode(statusResponse.qrCode);
				status = statusResponse.status;

				if (Date.now() - startTime >= maxTime) throw new Error("BankIdTimeout");

				if (status === "successful") break;
				await sleep(1000);
			}
			if (status === "successful") return startSignBankIdToken;

			await cancelSign(startSignBankIdToken);
		}

		if (Date.now() - startTime < maxTime) return await startOrder(signingToken, startTime);

		// If maxTime is surpassed, throw error
		throw new Error("BankIdTimeout");
	};

	const signFn = async (args: A) => {
		try {
			let startTime = Date.now();
			setStartTime(startTime);

			setOnExtendTime(() => () => {
				startTime = Date.now();
				setStartTime(startTime);
			});

			setIsSigning(true);
			const result = await request(args);
			if (!requiresSigning(result)) return result;

			const token = await startOrder(result.signingToken, startTime);
			const verifyResult = await verifySign(token);
			return verifyResult;
		} finally {
			resetSigningState();
		}
	};

	return signFn;
};

export const doRequestWithProductSigning =
	(args: {
		maxTime: number;
		initRequest: (applicationId: string) => Promise<ProductSigningStatus>;
		getStatusRequest: (applicationId: string) => Promise<ProductSigningStatus>;
		cancelRequest: (applicationId: string) => Promise<void>;
		setIsSigning: (isSigning: boolean) => void;
		setBankIdToken: (token: string | null) => void;
		setQrCode: (qrCode: string | null) => void;
		resetSigningState: () => void;
		setStartTime: (time: number) => void;
		setOnExtendTime: (f: () => void) => void;
		setOnCancel: (f: () => void) => void;
	}) =>
	async (applicationId: string) => {
		const {
			maxTime,
			initRequest,
			getStatusRequest,
			cancelRequest,
			setIsSigning,
			setBankIdToken,
			setQrCode,
			resetSigningState,
			setStartTime,
			setOnExtendTime,
			setOnCancel,
		} = args;
		try {
			setIsSigning(true);
			let startTime = Date.now();
			setStartTime?.(startTime);

			setOnExtendTime?.(() => () => {
				startTime = Date.now();
				setStartTime?.(startTime);
			});

			setOnCancel(() => () => cancelRequest(applicationId));

			const startOrder = async () => {
				let keepCalling = true;
				let status: ProductBankIdSignStatus = "pending";

				setTimeout(() => {
					keepCalling = false;
				}, 29000);

				while (keepCalling) {
					const { autoStartToken } = await initRequest(applicationId);
					setBankIdToken(autoStartToken);
					while (keepCalling) {
						const statusResponse = await getStatusRequest(applicationId);
						setQrCode(statusResponse.qrCode);
						status = statusResponse.status;

						if (status === "complete") break;

						if (Date.now() - startTime >= maxTime) throw new Error("BankIdTimeout");
						await sleep(1000);
					}
					if (status === "complete") return autoStartToken;

					await cancelRequest(applicationId);
				}

				if (Date.now() - startTime < maxTime) return await startOrder();

				// If maxTime is surpassed, throw error
				throw new Error("BankIdTimeout");
			};

			await startOrder();
		} finally {
			resetSigningState();
		}
	};
